import { useLocale } from "@hooks/useLocale";
import { useCurrentWell } from "@hooks/useCurrentWell";
import { QueryState } from "@components/ui/QueryState";
import { useGetWellSurveysQuery } from "@api/pApi";
import { SurveyStationsPanel } from "./SurveyStationsPanel";
import "./surveys.css";

/**
 * Surveys screen (§5) — the directional-survey program for the current well:
 * a station table (raw MD/Inc/Azi + read-only server-computed trajectory
 * columns), bulk paste-import, and the trajectory viz (plan / vertical section
 * / DLS). Reads GET /v1/wells/{uwi}/surveys (owned by Oxbo-P); the active event
 * supplies the stored unit profile + the report-open gating.
 */
export function Surveys() {
  const { t } = useLocale();
  const { well, activeEvent, isLoading: wellLoading, isError: wellError } = useCurrentWell();

  const {
    data: stations,
    isLoading: svLoading,
    isError: svError,
    refetch,
  } = useGetWellSurveysQuery(well?.uwi ?? "", { skip: !well });

  // The survey program is entered against the well's active job; edits gate on
  // that job being open (a closed event → read-only, matching the DDR lifecycle).
  const readOnly = !activeEvent || !!activeEvent.status_end;

  return (
    <div>
      <div className="page-head">
        <h1>{t("surveys.title")}</h1>
        <p className="subtitle">
          {well ? `${well.commonName} · ${well.field}` : t("well.notSelected")}
        </p>
      </div>
      <QueryState
        isLoading={wellLoading || svLoading}
        isError={wellError || svError}
        isEmpty={!well}
        emptyMessage={t("well.notSelected")}
        onRetry={refetch}
      >
        {well ? (
          <SurveyStationsPanel
            stations={stations ?? []}
            wellUwi={well.uwi}
            eventId={activeEvent?.id ?? ""}
            unitProfileKey={activeEvent?.unit_profile_key}
            readOnly={readOnly}
          />
        ) : null}
      </QueryState>
    </div>
  );
}
